import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PortfolioDashboard.css';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import AddToWishListModal from '../components/AddToWishListModal';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;
const POPUP_WIDTH = 320;

interface PortfolioSummary {
  totalValue: number;
  uniqueCards: number;
  totalCards: number;
  rlValue: number;
  rlCards: number;
  nonRlValue: number;
  nonRlCards: number;
  priceDate: string | null;
  totalValueChange: number | null;
  rlValueChange: number | null;
  nonRlValueChange: number | null;
}

interface HistoryPoint { date: string; total: number; rl: number; nonRl: number; }
interface ColorBreakdown  { colors: string; value: number; cards: number; }
interface RarityBreakdown { rarity: string; value: number; cards: number; }

interface TopHolding {
  cardId: string; name: string; setCode: string; setName: string;
  rarity: string; colors: string; quantity: number; price: number;
  holdingValue: number; reserved: boolean; imageSmall: string; imageLarge: string;
}

interface TopMover {
  cardId: string; name: string; setCode: string; rarity: string;
  quantity: number; pricePrev: number; priceNow: number;
  datePrev: string; dateNow: string;
  changePct: number; changeAbs: number; holdingChange: number;
  imageSmall: string; imageLarge: string;
}

const RARITY_COLORS: Record<string, string> = {
  mythic: '#e8954a', rare: '#c8a45a', uncommon: '#8ab4c8', common: '#9a9aaa', special: '#c8c8e0',
};
const COLOR_MAP: Record<string, { bg: string; label: string }> = {
  W:          { bg: '#e8e0c8', label: 'White' },
  U:          { bg: '#4a90d9', label: 'Blue' },
  B:          { bg: '#7a5a8a', label: 'Black' },
  R:          { bg: '#d05040', label: 'Red' },
  G:          { bg: '#3a8a5a', label: 'Green' },
  Colorless:  { bg: '#8a8a9a', label: 'Colorless' },
  Multicolor: { bg: '#c8a45a', label: 'Multicolor' },
};

function fmt(v: number) {
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtK(v: number) { return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : fmt(v); }

function formatDate(d: string | null): string {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function ColorPips({ colors }: { colors: string }) {
  if (!colors) return null;
  const map: Record<string, string> = { W: '#f5f0e0', U: '#4a90d9', B: '#8a6a9a', R: '#e05a4a', G: '#4a9a6a' };
  return (
    <div className="color-pips">
      {colors.split(',').filter(Boolean).map(c => (
        <span key={c} className="color-pip" style={{ background: map[c] || '#888' }} />
      ))}
    </div>
  );
}

// Sparkline SVG component with date labels
function Sparkline({ data, dates, color }: { data: number[]; dates: string[]; color: string }) {
  if (!data || data.length < 2) return null;
  const w = 300, h = 52, padX = 0, padY = 3, labelH = 14;
  const chartH = h - labelH;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = padX + (i / (data.length - 1)) * (w - padX * 2);
    const y = padY + (1 - (v - min) / range) * (chartH - padY * 2);
    return `${x},${y}`;
  });
  const pathD = `M ${pts.join(' L ')}`;
  const areaD = `M ${pts[0]} L ${pts.join(' L ')} L ${w},${chartH} L 0,${chartH} Z`;
  const trending = data[data.length - 1] >= data[0];
  const lineColor = trending ? '#2ecc71' : '#e74c3c';
  // Format date as "Dec 25"
  const fmtLabel = (d: string) => {
    if (!d) return '';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
  };
  const startLabel = dates.length > 0 ? fmtLabel(dates[0]) : '';
  const endLabel   = dates.length > 0 ? fmtLabel(dates[dates.length - 1]) : '';
  const uid = color.replace('#', '');
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="sparkline-svg"
      style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={`sg-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.3" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#sg-${uid})`} vectorEffect="non-scaling-stroke" />
      <path d={pathD} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round"
        strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {/* Date labels — use separate non-scaling text group */}
      <g style={{ fontSize: 10 }}>
        <text x="2" y={h - 2} fontSize="10" fill="#606080"
          fontFamily="Outfit,sans-serif" vectorEffect="non-scaling-stroke">{startLabel}</text>
        <text x={w - 2} y={h - 2} fontSize="10" fill="#606080"
          fontFamily="Outfit,sans-serif" textAnchor="end" vectorEffect="non-scaling-stroke">{endLabel}</text>
      </g>
    </svg>
  );
}

function MovementBadge({ change }: { change: number | null }) {
  if (change === null || change === undefined) return null;
  const up = change >= 0;
  return (
    <div className={`movement-badge ${up ? 'up' : 'down'}`}>
      <span className="movement-arrow">{up ? '▲' : '▼'}</span>
      <span className="movement-amount">{up ? '+' : ''}{fmt(change)}</span>
      <span className="movement-label">vs last week</span>
    </div>
  );
}

function BarChart({ items, total }: { items: { label: string; value: number; color: string }[]; total: number }) {
  return (
    <div className="bar-chart">
      {items.map((item, i) => (
        <div key={i} className="bar-row">
          <div className="bar-label">{item.label}</div>
          <div className="bar-track">
            <div className="bar-fill" style={{ width: `${(item.value / total) * 100}%`, background: item.color }} />
          </div>
          <div className="bar-value">{fmtK(item.value)}</div>
          <div className="bar-pct">{((item.value / total) * 100).toFixed(1)}%</div>
        </div>
      ))}
    </div>
  );
}

function HoverPopup({ imageUrl, x, y }: { imageUrl: string; x: number; y: number }) {
  const showLeft = x > window.innerWidth * 0.6;
  const top = Math.min(Math.max(y - 80, 10), window.innerHeight - Math.round(POPUP_WIDTH * 1.4) - 10);
  return (
    <div style={{
      position: 'fixed', top,
      left: showLeft ? x - POPUP_WIDTH - 16 : x + 20,
      width: POPUP_WIDTH, zIndex: 9999,
      pointerEvents: 'none', borderRadius: 10, overflow: 'hidden',
      boxShadow: '0 24px 60px rgba(0,0,0,0.85)',
      animation: 'popIn 0.1s ease',
    }}>
      <img src={imageUrl} alt="" style={{ width: '100%', display: 'block' }} />
    </div>
  );
}

export default function PortfolioDashboard() {
  const navigate = useNavigate();

  const [summary,   setSummary]   = useState<PortfolioSummary | null>(null);
  const [history,   setHistory]   = useState<HistoryPoint[]>([]);
  const [colors,    setColors]    = useState<ColorBreakdown[]>([]);
  const [rarities,  setRarities]  = useState<RarityBreakdown[]>([]);
  const [holdings,  setHoldings]  = useState<TopHolding[]>([]);
  const [movers,    setMovers]    = useState<TopMover[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState<'holdings' | 'movers'>('holdings');
  const [contextMenu, setContextMenu] = useState<{
    x: number; y: number; items: ContextMenuItem[];
  } | null>(null);
  const [wishListModal, setWishListModal] = useState<{
    cardId: string; cardName: string; imageUrl?: string | null;
  } | null>(null);
  const [hoverImg,  setHoverImg]  = useState<string | null>(null);
  const [hoverPos,  setHoverPos]  = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/portfolio/summary`),
      axios.get(`${API}/portfolio/history`),
      axios.get(`${API}/portfolio/colors`),
      axios.get(`${API}/portfolio/rarities`),
      axios.get(`${API}/portfolio/holdings?limit=20`),
      axios.get(`${API}/portfolio/movers?limit=20`),
    ]).then(([s, h, c, r, ho, m]) => {
      setSummary(s.data);
      setHistory(h.data);
      setColors(c.data);
      setRarities(r.data);
      setHoldings(ho.data);
      setMovers(m.data);
    }).catch(e => console.error('Portfolio load failed', e))
    .finally(() => setLoading(false));
  }, []);

  const handleRowContextMenu = (cardId: string, cardName: string, imageUrl: string | null, e: React.MouseEvent) => {
    e.preventDefault();
    setHoverImg(null);
    setHoverPos(null);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          icon: '🔍',
          label: 'View Card Detail',
          onClick: () => goToCard(cardId),
        },
        {
          icon: '📋',
          label: 'Add to Wish List',
          onClick: () => setWishListModal({ cardId, cardName, imageUrl }),
        },
      ],
    });
  };

  const goToCard = (cardId: string) =>
    navigate(`/cards/${cardId}`, { state: { from: '/portfolio' } });

  const showPopup = (img: string | null, e: React.MouseEvent) => {
    if (img) { setHoverImg(img); setHoverPos({ x: e.clientX, y: e.clientY }); }
  };
  const movePopup = (e: React.MouseEvent) => {
    if (hoverImg) setHoverPos({ x: e.clientX, y: e.clientY });
  };
  const hidePopup = () => { setHoverImg(null); setHoverPos(null); };

  if (loading) return <div className="portfolio-loading"><div className="portfolio-spinner" /></div>;
  if (!summary) return <div className="portfolio-error">Failed to load portfolio data.</div>;

  // Sparkline data series
  const histDates    = history.map(h => h.date);
  const totalSeries  = history.map(h => h.total);
  const rlSeries     = history.map(h => h.rl);
  const nonRlSeries  = history.map(h => h.nonRl);

  const colorChartItems = colors.map(c => {
    const parts = c.colors.split(',');
    if (parts.length > 1) return { label: 'Multi', value: c.value, color: '#c8a45a', key: 'multi' };
    const info = COLOR_MAP[c.colors] || COLOR_MAP['Colorless'];
    return { label: info.label, value: c.value, color: info.bg, key: c.colors };
  }).reduce((acc: { label: string; value: number; color: string; key: string }[], item) => {
    const existing = acc.find(a => a.key === item.key);
    if (existing) existing.value += item.value;
    else acc.push({ ...item });
    return acc;
  }, []).sort((a, b) => b.value - a.value);

  const rarityChartItems = rarities.map(r => ({
    label: r.rarity.charAt(0).toUpperCase() + r.rarity.slice(1),
    value: r.value,
    color: RARITY_COLORS[r.rarity] || '#888',
  }));

  const moverDateRange = movers.length > 0
    ? `${movers[0].datePrev} → ${movers[0].dateNow}`
    : null;

  return (
    <div className="portfolio-page">
      <div className="portfolio-header">
        <h1 className="portfolio-title">Portfolio</h1>
        {summary.priceDate && (
          <div className="portfolio-updated">Prices as of {formatDate(summary.priceDate)}</div>
        )}
      </div>

      {/* ── SUMMARY BUBBLES ────────────────────────────────── */}
      <div className="summary-grid">
        <div className="summary-card primary">
          <div className="summary-label">Total Collection Value</div>
          <div className="summary-value">{fmt(summary.totalValue)}</div>
          <div className="summary-sub">{summary.uniqueCards.toLocaleString()} unique · {summary.totalCards.toLocaleString()} total cards</div>
          <MovementBadge change={summary.totalValueChange} />
          <Sparkline data={totalSeries} dates={histDates} color="#c8a45a" />
        </div>
        <div className="summary-card rl">
          <div className="summary-label">Reserved List</div>
          <div className="summary-value">{fmt(summary.rlValue)}</div>
          <div className="summary-sub">{summary.rlCards} cards</div>
          <MovementBadge change={summary.rlValueChange} />
          <Sparkline data={rlSeries} dates={histDates} color="#e8954a" />
        </div>
        <div className="summary-card nonrl">
          <div className="summary-label">Non-Reserved List</div>
          <div className="summary-value">{fmt(summary.nonRlValue)}</div>
          <div className="summary-sub">{summary.nonRlCards.toLocaleString()} cards</div>
          <MovementBadge change={summary.nonRlValueChange} />
          <Sparkline data={nonRlSeries} dates={histDates} color="#8ab4c8" />
        </div>
      </div>

      {/* ── BREAKDOWNS ─────────────────────────────────────── */}
      <div className="breakdown-grid">
        <div className="breakdown-card">
          <div className="breakdown-title">Value by Colour</div>
          <BarChart items={colorChartItems} total={summary.totalValue} />
        </div>
        <div className="breakdown-card">
          <div className="breakdown-title">Value by Rarity</div>
          <BarChart items={rarityChartItems} total={summary.totalValue} />
        </div>
      </div>

      {/* ── TABLES ─────────────────────────────────────────── */}
      <div className="table-section">
        <div className="table-tabs">
          <button className={`table-tab ${activeTab === 'holdings' ? 'active' : ''}`}
            onClick={() => setActiveTab('holdings')}>Top 20 Holdings</button>
          <button className={`table-tab ${activeTab === 'movers' ? 'active' : ''}`}
            onClick={() => setActiveTab('movers')}>Top 20 Movers</button>
        </div>

        {activeTab === 'holdings' && (
          <div className="holdings-table">
            <div className="holdings-header">
              <span>#</span><span>Card</span><span>Set</span>
              <span>Qty</span><span>Price</span><span>Value</span>
            </div>
            {holdings.map((h, i) => (
              <button key={h.cardId} className="holding-row"
                onClick={() => goToCard(h.cardId)}
                onContextMenu={e => handleRowContextMenu(h.cardId, h.name, h.imageLarge || h.imageSmall || null, e)}
                onMouseLeave={hidePopup}
              >
                <span className="holding-rank">{i + 1}</span>
                <span className="holding-card"
                  onMouseEnter={e => showPopup(h.imageLarge || h.imageSmall, e)}
                  onMouseMove={movePopup}
                  onMouseLeave={e => { e.stopPropagation(); hidePopup(); }}
                >
                  {h.imageSmall && <img src={h.imageSmall} alt={h.name} className="holding-thumb" />}
                  <span className="holding-card-info">
                    <span className="holding-name">{h.name}</span>
                    <span className="holding-meta">
                      <ColorPips colors={h.colors} />
                      <span className="holding-rarity" style={{ color: RARITY_COLORS[h.rarity] || '#888' }}>
                        {h.rarity.charAt(0).toUpperCase()}
                      </span>
                      {h.reserved && <span className="rl-tag">RL</span>}
                    </span>
                  </span>
                </span>
                <span className="holding-set">{h.setCode.toUpperCase()}</span>
                <span className="holding-qty">×{h.quantity}</span>
                <span className="holding-price">{fmt(h.price)}</span>
                <span className="holding-value">{fmt(h.holdingValue)}</span>
              </button>
            ))}
          </div>
        )}

        {activeTab === 'movers' && (
          <div className="holdings-table">
            {moverDateRange && (
              <div className="movers-date-range">Comparing prices: {moverDateRange}</div>
            )}
            <div className="movers-header">
              <span>#</span><span>Card</span><span>Set</span><span>Qty</span>
              <span>Prev Price</span><span>Price</span><span>Change %</span><span>Change $</span><span>Portfolio Δ</span>
            </div>
            {movers.length === 0 ? (
              <div className="no-movers">
                Not enough price history to show movers yet.
              </div>
            ) : movers.map((m, i) => (
              <button key={m.cardId} className="holding-row mover-row"
                onClick={() => goToCard(m.cardId)}
                onContextMenu={e => handleRowContextMenu(m.cardId, m.name, m.imageLarge || m.imageSmall || null, e)}
                onMouseLeave={hidePopup}
              >
                <span className="holding-rank">{i + 1}</span>
                <span className="holding-card"
                  onMouseEnter={e => showPopup(m.imageLarge || m.imageSmall, e)}
                  onMouseMove={movePopup}
                  onMouseLeave={e => { e.stopPropagation(); hidePopup(); }}
                >
                  {m.imageSmall && <img src={m.imageSmall} alt={m.name} className="holding-thumb" />}
                  <span className="holding-card-info">
                    <span className="holding-name">{m.name}</span>
                  </span>
                </span>
                <span className="holding-set">{m.setCode.toUpperCase()}</span>
                <span className="holding-qty">×{m.quantity}</span>
                <span className="mover-prev-price" title={m.datePrev ? `As of ${formatDate(m.datePrev)}` : undefined}>
                  {fmt(m.pricePrev)}
                </span>
                <span className="holding-price">{fmt(m.priceNow)}</span>
                <span className={`mover-pct ${m.changePct >= 0 ? 'up' : 'down'}`}>
                  {m.changePct >= 0 ? '+' : ''}{m.changePct.toFixed(1)}%
                </span>
                <span className={`mover-abs ${m.changeAbs >= 0 ? 'up' : 'down'}`}>
                  {m.changeAbs >= 0 ? '+' : ''}{fmt(Math.abs(m.changeAbs))}
                </span>
                <span className={`mover-portfolio ${m.holdingChange >= 0 ? 'up' : 'down'}`}>
                  {m.holdingChange >= 0 ? '+' : ''}{fmt(Math.abs(m.holdingChange))}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {hoverImg && hoverPos && !contextMenu && <HoverPopup imageUrl={hoverImg} x={hoverPos.x} y={hoverPos.y} />}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={contextMenu.items}
          onClose={() => setContextMenu(null)}
        />
      )}
      {wishListModal && (
        <AddToWishListModal
          cardId={wishListModal.cardId}
          cardName={wishListModal.cardName}
          imageUrl={wishListModal.imageUrl}
          onDone={() => setWishListModal(null)}
          onCancel={() => setWishListModal(null)}
        />
      )}
    </div>
  );
}
